'use client';

import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { Thumb, DragState, UseScrollViewOptions, ScrollContentDrag } from '../atoms/scroll-view/scroll-view.types';
import { DEFAULT_MIN_THUMB_SIZE, EMPTY_DRAG_STATE, EMPTY_THUMB } from '../atoms/scroll-view/scroll-view.constants';
import { AxisEnum } from '../atoms/scroll-view/scroll-view.enum';
import { INTERACTIVE_SELECTOR } from './interactive-selectors';
const isMouseEvent = (e: React.SyntheticEvent<Element, Event>): e is React.MouseEvent<Element, MouseEvent> => {
    return 'clientX' in e && 'clientY' in e;
};

export const useScrollView = ({ axis, size, minThumbSize = DEFAULT_MIN_THUMB_SIZE }: UseScrollViewOptions) => {
    const isInteractiveElement = (el: HTMLElement | null) => {
        if (!el) return false;
        return !!el.closest(INTERACTIVE_SELECTOR);
    };
    const [overscrollX, setOverscrollX] = useState(0);
    const [overscrollY, setOverscrollY] = useState(0);

    const contentDragRef = useRef<ScrollContentDrag | null>(null);

    const contentRef = useRef<HTMLDivElement>(null);
    const trackXRef = useRef<HTMLDivElement>(null);
    const trackYRef = useRef<HTMLDivElement>(null);

    const [thumbX, setThumbX] = useState<Thumb>(EMPTY_THUMB);
    const [thumbY, setThumbY] = useState<Thumb>(EMPTY_THUMB);

    const dragRef = useRef<DragState>(EMPTY_DRAG_STATE);

    const { hasX, hasY } = useMemo(
        () => ({
            hasX: axis === AxisEnum.X || axis === AxisEnum.Both,
            hasY: axis === AxisEnum.Y || axis === AxisEnum.Both,
        }),
        [axis]
    );

    const wrapperStyle = useMemo<React.CSSProperties>(() => {
        if (axis === AxisEnum.X) {
            return { width: size, height: '100%' };
        }

        if (axis === AxisEnum.Y) {
            return { height: size, width: '100%' };
        }

        return { width: size, height: size };
    }, [axis, size]);

    const contentStyle = useMemo<React.CSSProperties>(() => {
        return {
            transform: `translate3d(${overscrollX}px, ${overscrollY}px, 0)`,
            transition: overscrollX || overscrollY ? 'none' : 'transform 0.25s cubic-bezier(0.2, 0, 0, 1)',
        };
    }, [overscrollX, overscrollY]);

    const getSizes = useCallback((ax: AxisEnum.X | AxisEnum.Y) => {
        const el = contentRef.current;
        const track = ax === AxisEnum.X ? trackXRef.current : trackYRef.current;
        if (!el || !track) return null;

        return {
            el,
            viewport: ax === AxisEnum.X ? el.clientWidth : el.clientHeight,
            total: ax === AxisEnum.X ? el.scrollWidth : el.scrollHeight,
            scrollPos: ax === AxisEnum.X ? el.scrollLeft : el.scrollTop,
            trackSize: ax === AxisEnum.X ? track.clientWidth : track.clientHeight,
        };
    }, []);

    const updateAxis = useCallback(
        (ax: AxisEnum.X | AxisEnum.Y) => {
            const sizes = getSizes(ax);
            if (!sizes) return;

            const { viewport, total, scrollPos, trackSize } = sizes;

            if (total <= viewport) {
                (ax === AxisEnum.X ? setThumbX : setThumbY)(s => ({ ...s, visible: false }));
                return;
            }

            const ratio = viewport / total;
            const size = Math.max(trackSize * ratio, minThumbSize);

            const maxScroll = total - viewport;
            const maxThumb = trackSize - size;
            const pos = (scrollPos / maxScroll) * maxThumb;

            (ax === AxisEnum.X ? setThumbX : setThumbY)({ size, pos, visible: true });
        },
        [getSizes, minThumbSize]
    );

    const update = useCallback(() => {
        if (hasX) updateAxis(AxisEnum.X);
        if (hasY) updateAxis(AxisEnum.Y);
    }, [hasX, hasY, updateAxis]);

    const onThumbDown = useCallback((e: React.PointerEvent, ax: AxisEnum.X | AxisEnum.Y) => {
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);

        const el = contentRef.current;
        if (!el) return;

        dragRef.current = {
            axis: ax,
            startPointer: ax === AxisEnum.X ? e.clientX : e.clientY,
            startScroll: ax === AxisEnum.X ? el.scrollLeft : el.scrollTop,
        };
    }, []);

    const onPointerMove = useCallback(
        (e: React.PointerEvent) => {
            const drag = dragRef.current;
            if (!drag.axis) return;

            const sizes = getSizes(drag.axis);
            if (!sizes) return;

            const { el, viewport, total, trackSize } = sizes;

            const delta = drag.axis === AxisEnum.X ? e.clientX - drag.startPointer : e.clientY - drag.startPointer;

            const thumbSize = drag.axis === AxisEnum.X ? thumbX.size : thumbY.size;
            const maxThumb = trackSize - thumbSize;
            const maxScroll = total - viewport;

            if (drag.axis === AxisEnum.X) {
                el.scrollLeft = drag.startScroll + (delta / maxThumb) * maxScroll;
            } else {
                el.scrollTop = drag.startScroll + (delta / maxThumb) * maxScroll;
            }
        },
        [getSizes, thumbX.size, thumbY.size]
    );

    const onPointerUp = useCallback((e: React.PointerEvent) => {
        dragRef.current = EMPTY_DRAG_STATE;
        e.currentTarget.releasePointerCapture(e.pointerId);
    }, []);

    const onTrackActivate = useCallback(
        (e: React.SyntheticEvent<Element, Event>, ax: AxisEnum.X | AxisEnum.Y) => {
            const sizes = getSizes(ax);
            if (!sizes) return;

            const { el, viewport, total, trackSize } = sizes;
            const track = ax === AxisEnum.X ? trackXRef.current : trackYRef.current;
            if (!track) return;

            const rect = track.getBoundingClientRect();

            let clickPos: number;

            if (isMouseEvent(e)) {
                clickPos = ax === AxisEnum.X ? e.clientX - rect.left : e.clientY - rect.top;
            } else {
                clickPos = trackSize / 2;
            }

            const ratio = clickPos / trackSize;
            const target = ratio * (total - viewport);

            if (ax === AxisEnum.X) el.scrollLeft = target;
            else el.scrollTop = target;
        },
        [getSizes]
    );

    const onThumbDownX = useCallback((e: React.PointerEvent) => onThumbDown(e, AxisEnum.X), [onThumbDown]);
    const onThumbDownY = useCallback((e: React.PointerEvent) => onThumbDown(e, AxisEnum.Y), [onThumbDown]);

    const onTrackClickX = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => onTrackActivate(e, AxisEnum.X),
        [onTrackActivate]
    );

    const onTrackClickY = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => onTrackActivate(e, AxisEnum.Y),
        [onTrackActivate]
    );

    const onWheel = useCallback(
        (e: WheelEvent) => {
            const el = contentRef.current;
            if (!el) return;

            if (hasX && !hasY) {
                if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
                    e.preventDefault();
                    el.scrollLeft += e.deltaY;
                }
            }
        },
        [hasX, hasY]
    );

    const onContentPointerDown = useCallback((e: React.PointerEvent) => {
        const el = contentRef.current;
        if (!el) return;

        const target = e.target as HTMLElement;
        if (isInteractiveElement(target)) {
            return;
        }
        el.setPointerCapture(e.pointerId);

        contentDragRef.current = {
            isDown: true,
            startX: e.clientX,
            startY: e.clientY,
            startScrollLeft: el.scrollLeft,
            startScrollTop: el.scrollTop,
        };
    }, []);

    const onContentPointerMove = useCallback(
        (e: React.PointerEvent) => {
            const el = contentRef.current;
            const drag = contentDragRef.current;
            if (!el || !drag?.isDown) return;

            const dx = e.clientX - drag.startX;
            const dy = e.clientY - drag.startY;

            const maxX = el.scrollWidth - el.clientWidth;
            const maxY = el.scrollHeight - el.clientHeight;

            const nextX = drag.startScrollLeft - dx;
            const nextY = drag.startScrollTop - dy;

            if (hasX) {
                if (nextX < 0) {
                    setOverscrollX(dx * 0.35);
                    el.scrollLeft = 0;
                } else if (nextX > maxX) {
                    setOverscrollX(dx * 0.35);
                    el.scrollLeft = maxX;
                } else {
                    setOverscrollX(0);
                    el.scrollLeft = nextX;
                }
            }

            if (hasY) {
                if (nextY < 0) {
                    setOverscrollY(dy * 0.35);
                    el.scrollTop = 0;
                } else if (nextY > maxY) {
                    setOverscrollY(dy * 0.35);
                    el.scrollTop = maxY;
                } else {
                    setOverscrollY(0);
                    el.scrollTop = nextY;
                }
            }
        },
        [hasX, hasY]
    );

    const onContentPointerUp = useCallback((e: React.PointerEvent) => {
        const el = contentRef.current;
        if (!el) return;

        contentDragRef.current = null;
        el.releasePointerCapture(e.pointerId);

        setOverscrollX(0);
        setOverscrollY(0);
    }, []);

    const onKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            const el = contentRef.current;
            if (!el) return;

            switch (e.key) {
                case 'ArrowUp':
                    if (hasY) el.scrollTop -= 40;
                    break;

                case 'ArrowDown':
                    if (hasY) el.scrollTop += 40;
                    break;

                case 'ArrowLeft':
                    if (hasX) el.scrollLeft -= 40;
                    break;

                case 'ArrowRight':
                    if (hasX) el.scrollLeft += 40;
                    break;

                case 'PageUp':
                    if (hasY) el.scrollTop -= el.clientHeight;
                    break;

                case 'PageDown':
                    if (hasY) el.scrollTop += el.clientHeight;
                    break;

                case 'Home':
                    if (hasY) el.scrollTop = 0;
                    if (hasX) el.scrollLeft = 0;
                    break;

                case 'End':
                    if (hasY) el.scrollTop = el.scrollHeight;
                    if (hasX) el.scrollLeft = el.scrollWidth;
                    break;

                default:
                    return;
            }

            e.preventDefault();
        },
        [hasX, hasY]
    );

    useLayoutEffect(() => {
        return () => {
            contentDragRef.current = null;
            dragRef.current = EMPTY_DRAG_STATE;
        };
    }, []);

    useLayoutEffect(() => {
        update();

        const el = contentRef.current;
        if (!el) return;

        el.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update);

        el.addEventListener('wheel', onWheel, { passive: false });

        return () => {
            el.removeEventListener('scroll', update);
            window.removeEventListener('resize', update);
            el.removeEventListener('wheel', onWheel);
        };
    }, [update, onWheel]);

    useLayoutEffect(() => {
        if (!trackXRef.current && !trackYRef.current) return;

        update();
    }, [update]);

    return {
        contentRef,
        trackXRef,
        trackYRef,
        wrapperStyle,
        contentStyle,
        thumbX,
        thumbY,
        hasX,
        hasY,
        onThumbDownX,
        onThumbDownY,
        onPointerMove,
        onPointerUp,
        onTrackClickX,
        onTrackClickY,
        onContentPointerDown,
        onContentPointerMove,
        onContentPointerUp,
        onKeyDown,
    };
};
